import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { HorizontalBarList } from "@/components/charts";
import { useGa4Channel, useGa4KeyEvents } from "@/hooks/useGa4";
import type { TimeRange } from "@/lib/time-range";

interface Props {
  range: TimeRange;
}

/**
 * Acquisition + business conversions. The key-events panel is Signature's
 * real FBO funnel: search → FBO click → call / email / callback / reservation.
 */
export function Ga4AcquisitionSection({ range }: Props) {
  const channel = useGa4Channel(range);
  const keyEvents = useGa4KeyEvents(range);

  const ke = keyEvents.data;
  const keErr = keyEvents.error ? panelErrorTitle(keyEvents.error) : undefined;
  const topConversion = ke?.keyEvents[0];

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="GA4 Acquisition & Conversions"
        source="ga4"
        description="Where visitors come from, and the actions that matter for the business — phoning an FBO, emailing one, requesting a callback, or starting a reservation."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricKpiTile label="Total conversions" value={ke ? ke.totalKeyEvents.toLocaleString("en-US") : "—"}
          delta="key actions taken in range" tone="emerald"
          hint="Every high-value action GA4 is set to count as a 'key event' — FBO calls, emails, callback requests, reservation clicks. The business outcomes, not just page views."
          isLoading={keyEvents.isLoading} errorTitle={keErr} />
        <MetricKpiTile label="Top conversion" value={topConversion ? topConversion.name : "—"}
          delta={topConversion ? `${topConversion.count.toLocaleString("en-US")} times` : undefined} tone="sky"
          hint="The single most-fired key event — usually 'view_search_results', i.e. visitors searching for an FBO."
          isLoading={keyEvents.isLoading} errorTitle={keErr} size="sm" />
        <MetricKpiTile label="Channels" value={channel.data ? channel.data.slices.length.toString() : "—"}
          delta="ways visitors arrive" tone="neutral"
          hint="How people reached the site — Direct, Organic Search, Paid Social, Email, Referral, etc. 'Direct' means they typed the URL or used a bookmark."
          isLoading={channel.isLoading} errorTitle={channel.error ? panelErrorTitle(channel.error) : undefined} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Sessions by channel">
          {channel.isLoading ? <PanelLoading />
            : channel.error ? <PanelError error={channel.error} onRetry={() => channel.refetch()} />
            : <HorizontalBarList tone="sky" limit={10}
                entries={(channel.data?.slices ?? []).map((s) => ({ label: s.label, value: s.sessions }))} />}
        </Card>

        <Card title="Business conversions (key events)">
          {keyEvents.isLoading ? <PanelLoading />
            : keyEvents.error ? <PanelError error={keyEvents.error} onRetry={() => keyEvents.refetch()} />
            : <HorizontalBarList tone="emerald" limit={10}
                entries={(ke?.keyEvents ?? []).map((e) => ({ label: e.name, value: e.count }))} />}
        </Card>
      </div>
    </section>
  );
}
